import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Guid } from 'guid-typescript';

import { SupplierStateService } from 'src/app/services/supplier-state.service';
import { GeolocationService } from 'src/app/services/geolocation.service';

import { SupplierStateModel } from 'src/app/models/supplier-state.model';
import { CoordinatesModel } from 'src/app/models/coordinates.model';
import { AddressModel } from 'src/app/models/address.model';
import { SupplierModel } from 'src/app/models/supplier.model';
import { PhoneModel } from 'src/app/models/phone.model';
import { AddressComponent } from 'src/app/shared/components/address/address.component';
import { AddressTypeEnum } from 'src/app/shared/enumerators/address-type.enum';

import { ContainerAnimations } from 'src/app/shared/animations/container-animation';
import { SupplierStatusEnum } from 'src/app/shared/enumerators/supplier-status-enum';
import { countryCodesList } from 'src/assets/data/countries';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { induMapStyles } from 'src/assets/data/map_styles_config';
import { ImageService } from 'src/app/shared/services/image.service';
import { UserService } from 'src/app/shared/services/user.service';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { GeolocationHelperService } from 'src/app/services/geolocation-helper.service';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { InduMapComponent } from '../../../shared/components/indu-map/indu-map.component';
import { MatInput, MatError, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { AddressComponent as AddressComponent_1 } from '../../../shared/components/address/address.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { SupplierSkillsComponent } from '../supplier-skills/supplier-skills.component';
import { SupplierWorkingDaysComponent } from '../supplier-hours/supplier-workingDays.component';
import { SupplierVehicleComponent } from '../supplier-vehicle/supplier-vehicle.component';
import { SupplierAssistantProfileComponent } from '../supplier-assistant/supplier-assistant.component';
import { SupplierWorkingAreasComponent } from '../supplier-working-areas/supplier-working-areas.component';

import { MatButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
    selector: 'app-supplier-profile',
    templateUrl: './supplier-profile.component.html',
    styleUrls: ['./supplier-profile.component.scss'],
    animations: [ContainerAnimations.height],
    imports: [MatIcon, InduMapComponent, FormsModule, ReactiveFormsModule, MatInput, MatError, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, AddressComponent_1, MatSlideToggle, SupplierSkillsComponent, SupplierWorkingDaysComponent, SupplierVehicleComponent, SupplierAssistantProfileComponent, SupplierWorkingAreasComponent, DatePipe, MatButton, PageHeaderComponent]
})
export class SupplierProfileComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly geolocationService = inject(GeolocationService);
  private readonly geolocationHelperService = inject(GeolocationHelperService);
  private readonly supplierService = inject(SupplierService);
  private readonly supplierStateSvc = inject(SupplierStateService);
  private readonly router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  toastr = inject(ToastService);
  svcImage = inject(ImageService);
  usrSvc = inject(UserService);

  supplierForm = new UntypedFormGroup({
    id: new UntypedFormControl(Guid.EMPTY),
    fullName: new UntypedFormControl('', [
      Validators.required,
      // Any letter (accents, ñ, ü...), spaces, apostrophes and hyphens: "Andrés Núñez", "O'Neill".
      Validators.pattern(/^[\p{L}\p{M}\s'-]+$/u),
    ]),
    dni: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern('^[0-9]{7,8}$'),
      Validators.minLength(7),
      Validators.maxLength(8),
    ]),
    email: new UntypedFormControl('', [
      Validators.required,
      // Any domain with one or more labels and a TLD of 2+ letters: .com, .com.ar, .gob.ar, .io...
      Validators.pattern(
        /^[a-z0-9._%+\-¿?¡!]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i
      ),
    ]),
    birthDate: new UntypedFormControl('', [
      Validators.required,
      this.validateBirthDate(),
    ]),
    phones: new UntypedFormArray([
      this.createRequiredPhone(),
      this.createOptionalPhone(),
    ]),
    stateId: new UntypedFormControl(2),
    addressesMatch: new UntypedFormControl(true),
    isActive: new UntypedFormControl(true),
  });

  supplierStates: SupplierStateModel[] = [];

  pAddress: string = 'personelAddress';
  lAddress: string = 'legalAddress';
  mapOptions = {
    mapTypeId: 'terrain',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    streetViewControl: false,
    mapTypeControl: false,
    styles: induMapStyles,
  };

  editProfile: boolean = false;
  supplierToEdit!: SupplierModel;

  countryCodesList: any[] = countryCodesList;
  selectedCode1: {} = {};
  selectedflag1: string = '';
  selectedCode2: {} = {};
  selectedflag2: string = '';
  maxDate: Date = new Date();

  coordinates: CoordinatesModel | undefined;
  disableCoordinates: boolean = true;
  coordinatesInterval: any;
  //google maps initialization test params
  addressPointer: any;
  expandedIndex: number = -1;

  center: any = {
    lat: -31.4492878,
    lng: -64.177427,
  };
  private readonly destroy$ = new Subject<void>();
  @ViewChild(AddressComponent, { static: false })
  addressComponent!: AddressComponent;
  supplierId: string = '';
  fragment: string = '';

  ngOnInit() {
    combineLatest([
      this.activatedRoute.queryParams,
      this.activatedRoute.fragment,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, fragment]) => {
        // The id stays in the URL: Back/Forward and reload must reopen this same supplier.
        this.supplierId = params['id'];
        if (fragment) this.fragment = fragment;
      });

    this.selectedflag1 = this.countryCodesList.find((x) => x.code == 'AR').src;
    this.selectedflag2 = this.countryCodesList.find((x) => x.code == 'AR').src;

    this.getSupplierStates();
    this.supplierForm.valueChanges.subscribe((model: SupplierModel) => {
      this.enableMapButton(model.addresses[0]);
    });

    this.supplierForm.controls['addressesMatch'].valueChanges.subscribe(
      (value: boolean) => {
        this.matchChange(value);
      }
    );
  }

  ngAfterViewInit(): void {
    if (this.supplierId) {
      this.supplierService.get(this.supplierId).subscribe((model) => {
        if (model.addresses.length > 0) {
          this.addMarker(model.addresses[0]);

          if (!model.addresses[0].alias) {
            model.addresses[0].alias = this.lAddress;
          }

          if (model.addresses[1]) {
            if (!model.addresses[1].alias) {
              model.addresses[1].alias = this.pAddress;
            }

            (
              this.supplierForm.controls['addresses'] as UntypedFormGroup
            ).controls[1].patchValue(model.addresses[1]);
          }
        }
        this.patchPhoneValues(model);
        this.supplierToEdit = model;
        this.supplierForm.patchValue(model);
      });
    } else {
      this.editProfile = true;
    }
  }

  moveToSection() {
    if (this.fragment !== '') {
      const element = document.getElementById(this.fragment);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.log('Element not found:', this.fragment); // Inexisting element
      }
    }
  }

  editSupplierProfile() {
    this.editProfile = true;
  }

  patchPhoneValues(model: SupplierModel) {
    model.phones.forEach((value: PhoneModel) => {
      value.number =
        value.areaCode != ' ' ? value.areaCode + value.number : value.number;
      value.countryCode =
        value.countryCode == '549' ? '+54' : value.countryCode;
      value.areaCode = ' ';
    });
  }

  validateOnlyNumbers(event: KeyboardEvent) {
    let key = event.key;
    if (['.', 'e', 'E', ',', '-', '+'].includes(key)) {
      event.preventDefault();
    }
  }

  validateBirthDate(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fechaIngresada = control.value as Date;
      const fechaActual = new Date();

      if (fechaIngresada > fechaActual) {
        return { validateBirthDate: true };
      }

      return null;
    };
  }

  changeSelection(countryCode: any, index: number) {
    switch (index) {
      case 0:
        this.selectedflag1 = this.countryCodesList.find(
          (x) => x.countryCode == countryCode
        ).src;
        break;
      case 1:
        this.selectedflag2 = this.countryCodesList.find(
          (x) => x.countryCode == countryCode
        ).src;
        break;
    }
  }

  getSupplierStates() {
    this.supplierStateSvc
      .getSupplierStates()
      .subscribe((data: SupplierStateModel[]) => {
        this.supplierStates = data;
        this.setIcons();
      });
  }

  matchChange(value: boolean) {
    if (!value && this.addressComponent.formArray.length > 1) {
      let index = this.getIndexByAlias(this.pAddress);

      this.addressComponent.formArray.at(index).reset();
      this.addressComponent.formArray
        .at(index)
        .patchValue(this.addressComponent.createAddress(this.pAddress).value);
    }

    // Si falla el submit y se tiene que agregar nuevamente el control
    if (this.addressComponent.formArray.length == 1) {
      this.addressComponent.addAddress(this.pAddress);
    }
  }

  onSubmit() {
    let legalAddressControl = this.getControlByAlias(this.lAddress);
    let addressToastShown = false;

    if (legalAddressControl) {
      if (legalAddressControl.valid) {
        if (this.supplierForm.controls['addressesMatch'].value as boolean) {
          let index = this.getIndexByAlias(this.pAddress);
          if (index != null) {
            this.addressComponent.formArray.removeAt(index);
          }
        }
      } else if (
        !legalAddressControl.controls.latitude.value ||
        !legalAddressControl.controls.longitude.value
      ) {
        addressToastShown = true;
        this.toastr.error(
          'Error',
          'Debe buscar la ubicación del domicilio ingresado.'
        );
      }
    }

    if (this.supplierForm.invalid) {
      // Show every missing or wrong field, not only the address toast.
      this.supplierForm.markAllAsTouched();
      if (!addressToastShown) {
        this.toastr.error('Error', 'Revise los campos obligatorios del formulario.');
      }
      return;
    }

    if (this.supplierForm.valid) {
      let model = this.supplierForm.value as SupplierModel;

      model.phones.forEach((value: PhoneModel, index: number) => {
        if (value.number.length == 0) {
          model.phones.splice(index, 1);
        } else {
          value.areaCode = ' ';
          value.number = value.number.toString();
        }
      });

      model.dni = model.dni.toString();

      if (model.id.toString() != Guid.EMPTY) {
        this.supplierService.put(model).subscribe({
          next: (data) => {
            this.supplierToEdit = data;
            this.toastr.success(
              'Actualización',
              'Se ha actualizado la información del prestador correctamente.'
            );
            this.editProfile = !this.editProfile;
          },
          error: (err) => {
            this.toastr.error(
              'Error',
              'No se ha podido actualizar la información del prestador.'
            );
          },
        });
      } else {
        this.supplierService.post(model).subscribe({
          next: (data) => {
            this.toastr.success(
              'Registración',
              'El prestador se ha registrado correctamente.'
            );
            this.supplierToEdit = data;
            this.editProfile = !this.editProfile;
            // Put the new supplier's id in the URL so a reload or Back/Forward reopens it, not an empty form.
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { id: data.id },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          },
          error: (err) => {
            let message =
              err.status == 400
                ? err.error.title
                : 'No se pudo registar el Prestador.';
            this.toastr.error('Error', message);
          },
        });
      }
    }
  }

  createRequiredPhone(): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(Guid.EMPTY),
      countryCode: new UntypedFormControl('+54', [Validators.required]),
      areaCode: new UntypedFormControl(' '),
      number: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{10,}$'),
      ]),
      isMobile: new UntypedFormControl(false),
      providerId: new UntypedFormControl(Guid.EMPTY),
    });
  }

  createOptionalPhone(): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(Guid.EMPTY),
      countryCode: new UntypedFormControl('+54'),
      areaCode: new UntypedFormControl(' '),
      number: new UntypedFormControl('', [Validators.pattern('^[0-9]{10,}$')]),
      isMobile: new UntypedFormControl(false),
      providerId: new UntypedFormControl(Guid.EMPTY),
    });
  }

  getCountryName(countryId: Guid) {
    return this.addressComponent.countries.find((x) => x.id == countryId)
      ?.description;
  }

  getStateName(statesId: Guid) {
    return this.addressComponent.states.find((x) => x.id == statesId)
      ?.description;
  }

  getLocationName(locationsId: Guid) {
    return this.addressComponent.locations.find((x) => x.id == locationsId)
      ?.description;
  }

  setIcons(): void {
    this.supplierStates.forEach((s) => {
      switch (s.id) {
        case SupplierStatusEnum.Online:
          s.icon = 'visibility';
          break;
        case SupplierStatusEnum.Offline:
          s.icon = 'visibility_off';
          break;
        case SupplierStatusEnum.Working:
          s.icon = 'check_circle_outline';
          break;
        case SupplierStatusEnum.Going:
          s.icon = 'highlight_off';
          break;
        case SupplierStatusEnum.Paused:
          s.icon = 'exit_to_app';
          break;
      }
    });
  }

  addMarker(model: any) {
    const { markers, center } = this.geolocationHelperService.generateMarker({
      lat: model.latitude,
      long: model.longitude,
    });

    this.addressPointer = markers;
    if (center) {
      this.center = center;
    }
  }

  markerDragend(marker: any) {
    this.addressComponent.setCoordinates(
      AddressTypeEnum.LegalAddress,
      marker.lat,
      marker.lng
    );
    this.center = {
      lat: marker.lat,
      lng: marker.lng,
    };
  }

  addPointerMarker() {
    this.geolocationHelperService.addPointerMarker(
      this.addressComponent,
      this.getCoordinates.bind(this)
    );
  }

  getCoordinates(
    street: string,
    streetNumber: number,
    city: any,
    state: any,
    country: any
  ) {
    this.geolocationHelperService
      .getAndSetCoordinates(
        street,
        streetNumber,
        city,
        state,
        country,
        this.addressComponent
      )
      .subscribe((data: CoordinatesModel) => {
        this.coordinates = data;
        this.addMarker({ latitude: data.lat, longitude: data.long });
      });
  }

  enableMapButton(model: AddressModel) {
    if (
      model?.street &&
      model.street.length > 0 &&
      model.number > 0 &&
      model.cityId &&
      Guid.isGuid(model.cityId) &&
      model.stateId &&
      Guid.isGuid(model.stateId) &&
      model.countryId &&
      Guid.isGuid(model.countryId)
    ) {
      this.disableCoordinates = false;
    } else {
      this.disableCoordinates = true;
    }
  }

  getIndexByAlias(alias: string): any {
    let index = null;
    for (let i = 0; this.addressComponent.formArray.length > i; i++) {
      if (this.addressComponent.formArray.value[i].alias == alias) {
        index = i;
        break;
      }
    }
    return index;
  }

  getControlByAlias(alias: string): any {
    let control = null;
    for (let i = 0; this.addressComponent.formArray.length > i; i++) {
      if (this.addressComponent.formArray.value[i].alias == alias) {
        control = this.addressComponent.formArray.controls[i];
        break;
      }
    }
    return control;
  }

  keyOnAddress(event: KeyboardEvent, index: number) {
    if (event.key === ' ') {
      this.toggleVisibility(index);
      event.preventDefault();
    }
  }

  toggleVisibility(index: number) {
    if (this.expandedIndex === index) {
      this.expandedIndex = -1; // Contraer el elemento actualmente expandido
    } else {
      this.expandedIndex = index; // Expandir el nuevo elemento
    }
  }

  backToList() {
    this.router.navigate(['/supplierList']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
